<template>
	<div id="top">
		<nav class="main-nav" :class="{ 'show-mobile-nav': showMobileNav }">
			<div class="row">
				<div class="columns column12">
					<div class="nav-bar">
						<nuxt-link
							class="logo"
							:to="{ path: localePath('/'), hash: route.name.startsWith('index') ? '#top' : '' }"
						/>
						<div class="nav-content">
							<ul>
								<li class="menu-close" @click="hideMobileNav">
									<font-awesome-icon :icon="`fal fa-times`" size="1x" />
								</li>
								<li v-for="item in topmenu[locale]" :key="item.filename" @click="hideMobileNav">
									<nuxt-link :to="item.filename">
										{{ item.header }}
									</nuxt-link>
								</li>
								<li class="lang-nav mobile-only">
									<div v-for="item in langmenu" :key="item.filename" class="language">
										<nuxt-link
											class="flag"
											:class="item.language"
											:to="item.filename"
											@click="hideMobileNav"
										>
											<span>{{ item.language }}</span>
										</nuxt-link>
									</div>
								</li>
							</ul>
						</div>
						<div class="nav-links">
							<a :href="`tel:${$phoneRaw(defaults[locale].website.phone)}`" class="desktop-only">
								<font-awesome-icon :icon="`fal fa-phone`" size="1x" />
							</a>
							<a :href="`mailto:${defaults[locale].website.email}`" class="desktop-only">
								<font-awesome-icon :icon="`fal fa-envelope`" size="1x" />
							</a>
						</div>
						<div v-if="page" class="language-navigation" @click="toggleLanguageMenu">
							<div v-if="langmenu.length" class="lang-select">
								<span class="language flag" :class="locale">{{ locale }}</span>
								<font-awesome-icon :icon="`fal fa-chevron-down`" size="1x" />
								<div class="lang-wrapper lang-nav" :class="{ active: showLanguageMenu }">
									<div v-for="item in langmenu" :key="item.filename" class="language">
										<nuxt-link
											class="flag"
											:class="item.language"
											:to="item.filename"
											@click="hideMobileNav"
										>
											<span>{{ item.language }}</span>
										</nuxt-link>
									</div>
								</div>
							</div>
						</div>
						<div class="buttons">
							<div class="menu-button mobile-only" @click="toggleMobileNav">
								<font-awesome-icon class="icon" icon="fal fa-bars" size="1x" />
							</div>
							<nuxt-link class="button book-now cta reserve-now" :to="localePath('contact', locale)">
								<font-awesome-icon class="icon" icon="fal fa-phone" size="1x" />
								<span>{{ $t('bookNow') }}</span>
							</nuxt-link>
						</div>
					</div>
				</div>
			</div>
		</nav>
	</div>
</template>

<script setup>
const { page, fetchPage } = usePage();
const { defaults, fetchDefaults } = useDefaults();
const { locale } = useI18n();
const localePath = useLocalePath();
const route = useRoute();

await fetchPage();

if (!defaults.value) {
	await fetchDefaults();
}

const showMobileNav = ref(false);
const showLanguageMenu = ref(false);

const { data: topmenu } = await useWebsiteFetch('topmenu');

const langmenu = computed(() => {
	return (page.value && page.value.langmenu) ?? [];
});

const toggleMobileNav = () => {
	showMobileNav.value = !showMobileNav.value;
};

const hideMobileNav = () => {
	showMobileNav.value = false;
};

const toggleLanguageMenu = () => {
	showLanguageMenu.value = !showLanguageMenu.value;
};
</script>

<style lang="scss" scoped>
.mobile-only {
	display: none !important;
}

.main-nav {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	z-index: 1000;
	background: var(--nav-background-color);
	backdrop-filter: blur(12px);
	box-shadow: 0 0 20px rgb(0 0 0 / 10%);

	ul li {
		display: inline-block;
	}
}

.nav-links {
	color: #fff;

	a {
		margin: 0 7px;
		color: #fff;

		&:hover {
			color: var(--cta-color);
		}
	}
}

.nav-bar {
	display: flex;
	align-items: center;
	padding: 8px 0;
	z-index: 9;
	position: relative;

	.buttons {
		display: flex;
		align-items: center;
		gap: 10px;

		.button {
			padding: 15px 25px 12px;
			margin: 0;
			display: flex;
			justify-content: center;
			align-items: center;
		}

		.icon {
			display: none;
			margin-right: 5px;
		}
	}
}

.logo {
	padding: 0;
	margin: 8px 0;
	text-decoration: none;
	width: 150px;
	height: 80px;
	background: url('~/assets/images/logo-donfoodandevents-white-full.png') no-repeat center center;
	background-size: 150px;
}

.nav-content {
	flex-grow: 1;
	text-align: right;

	a {
		text-decoration: none;
		margin: 0 12px;
		color: #fff;
		letter-spacing: 1px;
		font-size: 12px;
		text-transform: uppercase;
		font-weight: 500;
		border-bottom: 2px solid rgb(0 0 0 / 0%);
		padding: 0 0 3px;

		&.active,
		&:hover {
			color: var(--cta-color);
			border-color: #fff;
		}

		&.router-link-active,
		&.router-link-exact-active {
			color: var(--cta-color);
		}
	}

	.menu-close {
		color: #000;
		display: none;
		font-size: 30px;
		cursor: pointer;
		position: absolute;
		top: 15px;
		width: auto;
		right: 20px;
		padding: 0;
		margin: 0;
	}
}

.lang-nav {
	margin: 0 0 25px;

	.language {
		margin: 20px 0;

		.flag::before {
			content: '';
			width: 20px;
			position: relative;
			top: 3px;
			height: 20px;
			margin-right: 6px;
			display: inline-block;
			background-size: 20px;
		}

		.flag.en::before {
			background: url('~/assets/images/en.png') no-repeat;
			background-size: 20px;
		}

		.flag.nl::before {
			background: url('~/assets/images/nl.png') no-repeat;
			background-size: 20px;
		}

		.flag.de::before {
			background: url('~/assets/images/de.png') no-repeat;
			background-size: 20px;
		}

		.flag.fr::before {
			background: url('~/assets/images/fr.png') no-repeat;
			background-size: 20px;
		}

		.flag.es::before {
			background: url('~/assets/images/es.png') no-repeat;
			background-size: 20px;
		}
	}

	img {
		width: 20px;
		float: left;
		margin: 0 5px 0 0;
	}

	a {
		width: 100%;
		float: left;
		color: var(--body-color);
		text-decoration: none;
		margin: 4px 0;
		text-transform: uppercase;

		&.active {
			color: var(--cta-color);
		}
	}
}

.language-navigation:hover .lang-nav-sel,
.language-navigation:hover .fa-globe {
	color: var(--cta-color);
}

.lang-nav-sel {
	box-shadow: none;
	cursor: pointer;
	width: 30px;
	padding: 0 4px 0 0;
	color: #fff;
	border: none;
	text-transform: uppercase;
	font-size: 12px;
	font-family: sans-serif;
	appearance: none;
	background: url('~/assets/images/dropdown.png') right 1px center no-repeat;
	background-size: 7px;
	transition: color 0.3s ease-in-out;

	option {
		color: var(--body-color);
	}

	&::-ms-expand {
		display: none;
	}
}

.lang-select {
	padding: 8px 20px;
	margin: 0 10px;
	border-left: 1px solid #e5e5e5;
	position: relative;
	cursor: pointer;
	text-transform: uppercase;
	font-size: 13px;
	display: flex;
	flex-flow: row nowrap;
	align-items: center;

	> .flag::before {
		content: '';
		width: 20px;
		position: relative;
		top: 5px;
		height: 20px;
		display: inline-block;
		background-size: 20px;
		margin: 0 9px 0 0;
	}

	> .flag.en::before {
		background: url('~/assets/images/en.png') no-repeat;
		background-size: 20px;
	}

	> .flag.nl::before {
		background: url('~/assets/images/nl.png') no-repeat;
		background-size: 20px;
	}

	> .flag.de::before {
		background: url('~/assets/images/de.png') no-repeat;
		background-size: 20px;
	}

	> .flag.fr::before {
		background: url('~/assets/images/fr.png') no-repeat;
		background-size: 20px;
	}

	> .flag.es::before {
		background: url('~/assets/images/es.png') no-repeat;
		background-size: 20px;
	}

	span {
		margin: 8px 4px -2px;
		vertical-align: bottom;
		color: #fff;
	}

	> span {
		margin: -4px 4px 0;
	}

	a {
		text-decoration: none;
	}

	svg {
		font-size: 12px;
		color: #fff;
	}
}

.lang-wrapper {
	display: none;
	position: absolute;
	top: 35px;
	left: 3px;
	right: 3px;
	padding: 10px 9px;
	text-align: center;
	background: #444d53;
	box-shadow: 0 18px 18px rgb(0 0 0 / 10%);
	z-index: 90;
	color: #fff;

	&.active,
	a {
		display: flex;
		flex-flow: column nowrap;
		place-content: space-between center;
		color: #000 !important;
		font-size: 13px;
		gap: 5px;
	}

	a {
		width: 100%;
		flex-direction: row;
	}

	img {
		width: 16px;
		height: 16px;
		margin: 2px 0 0;
	}

	.language {
		margin: 0;
	}
}

@media (max-width: 1280px) {
	.nav-bar a {
		font-size: 11px;
	}
}

@media (max-width: 1200px) {
	.nav-bar .buttons {
		.button {
			padding: 16px;
		}

		.menu-button {
			margin-right: 5px;

			.icon {
				font-size: 26px;
			}
		}

		.icon {
			margin-right: 0;
			display: unset;
			font-size: 16px;
		}

		span {
			display: none;
		}
	}

	.menu-button {
		text-transform: uppercase;
		display: flex;
		flex-flow: row nowrap;
		align-items: center;
		justify-content: space-between;
		margin: 0 10px 0 0;
		cursor: pointer;
		color: #fff;

		svg {
			font-size: 22px;
		}

		span {
			margin: 0 0 0 5px;
		}
	}

	.desktop-only {
		display: none !important;
	}

	.mobile-only {
		display: flex !important;

		&.lang-nav {
			display: inline-block !important;
			margin: 30px 0 0;
			padding: 0 10px;
		}
	}

	.language-navigation {
		display: none;
	}

	.main-nav {
		position: fixed !important;
		top: 0;
		box-shadow: 0 0 10px rgb(0 0 0 / 10%);

		&.show-mobile-nav {
			.nav-content ul {
				display: flex;
				flex-direction: column;
				justify-content: center;
				opacity: 1;
				visibility: visible;
				transition: opacity 0.3s ease-in-out;

				li:not(.menu-close) {
					width: 100%;
				}

				li:not(.lang-nav, .menu-close) {
					margin: 7px 0;

					a {
						font-size: 20px;
					}
				}
			}
		}

		ul {
			position: fixed;
			inset: 0;
			background: rgb(255 255 255 / 85%);
			backdrop-filter: blur(17px);
			text-align: center;
			padding: 20px;
			opacity: 0;
			visibility: hidden;
			overflow-y: scroll;
			height: 100vh;

			a {
				color: var(--body-color);
			}
		}
	}

	.lang-nav {
		a {
			float: none;
		}

		.language {
			margin: 5px 0;
		}
	}

	.nav-content .menu-close {
		display: block;
	}

	.logo {
		width: 105px;
		height: 56px;
		background: url('~/assets/images/logo-donfoodandevents-white-small.png') no-repeat center center;
		background-size: 105px;
	}
}

@media (max-width: 580px) {
	.nav-bar .buttons span {
		display: none;
	}
}
</style>
